import { useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { deviceType, isMobile } from 'react-device-detect'
import {
  handleCheckoutError,
  isEmpty,
  makeCheckError,
  makeDeviceType,
} from '@open-tender/utils'
import {
  resetCheck,
  resetConfirmation,
  resetErrors,
  resetTip,
  selectBrand,
  selectCartTotal,
  selectCheckout,
  selectConfirmationOrder,
  selectContent,
  selectCustomer,
  selectMenuSlug,
  selectOrder,
  setConfirmationOrder,
  setDeviceType,
  setSubmitting,
  validateOrder,
  useAppDispatch,
  useAppSelector,
  fetchLocation,
  openModal,
  updateForm,
  updateCheckoutCustomer,
  resetOrder,
  resetGroupOrder,
  selectConfig,
  resetQuote,
  selectQuote,
} from '@open-tender/cloud'
import { CustomerCreate } from '@open-tender/types'
import { FormError, Content, Main } from 'components'
import CheckoutCancelEdit from './CheckoutCancelEdit'
import CheckoutCustomer from './CheckoutCustomer'
import CheckoutGuest from './CheckoutGuest'
import CheckoutPickup from './CheckoutPickup'
import CheckoutDelivery from './CheckoutDelivery'
import CheckoutCart from './CheckoutCart'
import CheckoutHeader from './CheckoutHeader'
import CheckoutAddress from './CheckoutAddress'
import CheckoutDetails from './CheckoutDetails'
import CheckoutCurbside from './CheckoutCurbside'
import CheckoutSurcharges from './CheckoutSurcharges'
import CheckoutDiscounts from './CheckoutDiscounts'
import CheckoutPromoCodes from './CheckoutPromoCodes'
import CheckoutTip from './CheckoutTip'
import CheckoutGiftCards from './CheckoutGiftCards'
import CheckoutTenders from './CheckoutTenders'
import CheckoutSection from './CheckoutSection'
import CheckoutSubmit from './CheckoutSubmit'
import {
  makeAddressError,
  makeCustomerError,
  makeFormTitle,
  makeTipsError,
} from './Checkout.utils'
import {
  CheckoutContent,
  CheckoutInfo,
  CheckoutSidebar,
  CheckoutSidebarContent,
  CheckoutTitle,
  CheckoutView,
} from './Checkout.styled'
import CheckoutValutec from './CheckoutValutec'
import { sendCheckoutView } from 'app/analytics'
import { initKount } from 'utils/helpers'

const Checkout = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { title, has_promo_codes, has_gift_cards, has_valutec } =
    useAppSelector(selectBrand) || {}
  const { settings } = useAppSelector(selectConfig) || {}
  const kount = settings?.kount
  const { checkout: config } = useAppSelector(selectContent) || {}
  const cartTotal = useAppSelector(selectCartTotal)
  const menuSlug = useAppSelector(selectMenuSlug)
  const { serviceType, revenueCenter, requestedAt } =
    useAppSelector(selectOrder)
  const { revenue_center_id: revenueCenterId } = revenueCenter || {}
  const { auth, profile } = useAppSelector(selectCustomer)
  const hasCustomer = !!auth
  const { check, form, errors, submitting, completedOrder } =
    useAppSelector(selectCheckout)
  const hasGiftCards =
    has_gift_cards && check?.config.tender_types.includes('GIFT_CARD')
  const hasValutec =
    has_valutec && check?.config.tender_types.includes('GIFT_CARD')
  const confirmedOrder = useAppSelector(selectConfirmationOrder)
  const hasConfirmationOrder = !!confirmedOrder
  const hasCheck = !!check
  const hasFormCustomer = !isEmpty(form.customer)
  const quote = useAppSelector(selectQuote)
  const formError = errors ? (errors.form as string) || null : null
  const customerError = makeCustomerError(errors)
  const checkoutError = customerError || formError
  const addressError = makeAddressError(errors)
  const tipsError = makeTipsError(errors)
  const deviceTypeName = makeDeviceType(deviceType)
  const { formTitle, hasAddress } = makeFormTitle(check, serviceType)
  const checkError = check?.errors ? makeCheckError(check.errors) : null
  const errMessages = checkError ? handleCheckoutError(checkError) : {}
  const unknownError = (errors?.unknown as string) || null
  const cartError = (errMessages.cart as string) || null
  const isMissingNames = auth && (!profile?.first_name || !profile.last_name)
  const isQuoteOrder = Boolean(quote.order)
  useEffect(() => {
    if (!kount || !kount.enabled) return
    const { clientId, environment } = kount
    initKount(clientId, environment, ({ SessionID }) =>
      dispatch(updateForm({ kount_device_session_id: SessionID }))
    )
  }, [dispatch, kount])

  useEffect(() => {
    if (!submitting && formError) window.scrollTo(0, 0)
  }, [formError, submitting])

  useEffect(() => {
    if (revenueCenterId) {
      const total = cartTotal.toFixed(2)
      dispatch(fetchLocation({ revenueCenterId, cartTotal: total }))
    }
  }, [revenueCenterId, cartTotal, dispatch])

  useEffect(() => {
    dispatch(setSubmitting(false))
    dispatch(resetConfirmation())
    dispatch(setDeviceType(deviceTypeName))
    return () => {
      dispatch(resetErrors())
      dispatch(resetTip())
      dispatch(resetCheck())
    }
  }, [dispatch, deviceTypeName])
  const checkLoaded = check && Boolean(check.cart)
  useEffect(() => {
    if (checkLoaded) sendCheckoutView(check)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkLoaded])

  const updateCustomer = useCallback(
    async (data: CustomerCreate) => {
      const { first_name, last_name, email, phone, company } = data
      const customer = {
        customer_id: profile?.customer_id,
        first_name,
        last_name,
        email,
        phone,
        company,
      }
      await dispatch(updateForm({ customer }))
      await dispatch(updateCheckoutCustomer({ customer }))
      dispatch(validateOrder())
    },
    [dispatch, profile?.customer_id]
  )

  const openUpdateCustomerModal = useCallback(() => {
    dispatch(openModal({ type: 'profile', args: { callback: updateCustomer } }))
  }, [dispatch, updateCustomer])

  useEffect(() => {
    if (isMissingNames) {
      openUpdateCustomerModal()
      return
    }
    if (!completedOrder && hasFormCustomer && !hasCheck) {
      dispatch(validateOrder())
    }
  }, [
    dispatch,
    completedOrder,
    hasFormCustomer,
    hasCheck,
    isMissingNames,
    openUpdateCustomerModal,
  ])

  useEffect(() => {
    if (isMissingNames) {
      openUpdateCustomerModal()
      return
    }
    if (hasCheck) {
      dispatch(validateOrder())
    }
  }, [dispatch, hasCheck, isMissingNames, openUpdateCustomerModal, requestedAt])

  useEffect(() => {
    const confirmOrder = async () => {
      dispatch(setConfirmationOrder(completedOrder))
      await dispatch(resetOrder())
      await dispatch(resetQuote())
      await dispatch(resetGroupOrder())
      navigate(`/confirmation${isQuoteOrder ? '?is_quote=true' : ''}`)
    }
    if (completedOrder) {
      if (!hasConfirmationOrder) {
        confirmOrder()
      }
    } else if (cartTotal === 0) {
      navigate(menuSlug)
    } else if (!revenueCenterId || !serviceType) {
      navigate('/account')
    } else if (!hasCustomer && !hasFormCustomer) {
      navigate('/checkout/guest')
    }
  }, [
    dispatch,
    navigate,
    cartTotal,
    menuSlug,
    revenueCenterId,
    serviceType,
    completedOrder,
    hasCustomer,
    hasFormCustomer,
    hasConfirmationOrder,
    isQuoteOrder,
  ])

  return (
    <>
      <Helmet>
        <title>Checkout | {title}</title>
      </Helmet>
      <Content>
        <CheckoutHeader />
        <Main bgColor="checkout" style={{ overflow: 'hidden' }}>
          <CheckoutView>
            <CheckoutContent>
              <CheckoutTitle>
                <h1>{config?.title}</h1>
                <p>{config?.subtitle}</p>
                <CheckoutCancelEdit />
                {checkoutError && <FormError errMsg={checkoutError} />}
                {addressError && <FormError errMsg={addressError} />}
                {cartError && <FormError errMsg={cartError} />}
                {tipsError && <FormError errMsg={tipsError} />}
                {unknownError && <FormError errMsg={unknownError} />}
              </CheckoutTitle>
              <CheckoutInfo>
                {auth ? <CheckoutCustomer /> : <CheckoutGuest />}
                {check && (
                  <>
                    {['WALKIN', 'PICKUP'].includes(serviceType ?? 'WALKIN') ? (
                      <CheckoutPickup />
                    ) : serviceType === 'DELIVERY' ? (
                      <CheckoutDelivery />
                    ) : null}
                  </>
                )}
              </CheckoutInfo>
              {formTitle && (
                <CheckoutSection title={formTitle} style={{ padding: '0' }}>
                  <CheckoutAddress />
                  <CheckoutDetails hasAddress={hasAddress} />
                </CheckoutSection>
              )}
              {check && (
                <>
                  <CheckoutCurbside setInitialValue={!formTitle} />
                  <CheckoutSurcharges />
                  <CheckoutDiscounts />
                  {has_promo_codes && <CheckoutPromoCodes />}
                  <CheckoutTip />
                  {isMobile && (
                    <CheckoutCart
                      highlightServiceFee={config?.highlightServiceFee}
                    />
                  )}
                  {hasGiftCards && <CheckoutGiftCards />}
                  {hasValutec && <CheckoutValutec />}
                  <CheckoutTenders />
                  <CheckoutSubmit />
                </>
              )}
            </CheckoutContent>
            <CheckoutSidebar>
              <CheckoutSidebarContent>
                {!isMobile && check && (
                  <CheckoutCart
                    highlightServiceFee={config?.highlightServiceFee}
                  />
                )}
              </CheckoutSidebarContent>
            </CheckoutSidebar>
          </CheckoutView>
        </Main>
      </Content>
    </>
  )
}

export default Checkout
